/**
 * Port of WritingReferenceLetterTemplates.swift.
 * Templates stay separate from rendering. Coordinates are normalized 0…1.
 */

import {
  arc,
  cubic,
  dot,
  ellipse,
  joined,
  line,
  poly,
  svgPathToPolyline,
  variant,
} from "./writingPathBuilders";
import { GLYPH_STROKES } from "../../../data/glyphStrokes";

const letter = (character, letterCase, strokes, extras = {}) => ({
  character,
  letterCase,
  variants: [variant("school-print", strokes, extras)],
});

const letterVariants = (character, letterCase, variants) => ({
  character,
  letterCase,
  variants,
});

const drawing = (id, displayName, displayText, category, aspectRatio, strokes, extras = {}) => ({
  id,
  displayName,
  displayText,
  category,
  aspectRatio,
  variants: [variant("school-print", strokes, extras)],
});

const UPPERCASE = [
  letter("A", "uppercase", [line(0.49, 0.12, 0.18, 0.86), line(0.49, 0.12, 0.82, 0.86), line(0.31, 0.58, 0.68, 0.58, "crossbar")], { enclosures: 1, intersections: 2 }),
  letter("B", "uppercase", [line(0.24, 0.12, 0.24, 0.86), cubic(0.24, 0.12, 0.84, 0.08, 0.84, 0.48, 0.24, 0.48, "primary", true), cubic(0.24, 0.48, 0.9, 0.46, 0.88, 0.88, 0.24, 0.86, "primary", true)], { enclosures: 2 }),
  letter("C", "uppercase", [arc(0.52, 0.49, 0.34, 0.38, 315, 45)]),
  letter("D", "uppercase", [line(0.24, 0.12, 0.24, 0.86), cubic(0.24, 0.12, 0.92, 0.1, 0.92, 0.88, 0.24, 0.86, "primary", true)], { enclosures: 1 }),
  letter("E", "uppercase", [line(0.24, 0.12, 0.24, 0.86), line(0.24, 0.12, 0.78, 0.12, "crossbar"), line(0.24, 0.49, 0.68, 0.49, "crossbar"), line(0.24, 0.86, 0.8, 0.86, "crossbar")], { intersections: 3 }),
  letter("F", "uppercase", [line(0.25, 0.12, 0.25, 0.86), line(0.25, 0.12, 0.79, 0.12, "crossbar"), line(0.25, 0.49, 0.68, 0.49, "crossbar")], { intersections: 2 }),
  letter("G", "uppercase", [arc(0.52, 0.49, 0.34, 0.38, 315, 45), joined([line(0.78, 0.53, 0.56, 0.53), line(0.78, 0.53, 0.78, 0.72)], "crossbar")], { intersections: 1 }),
  letter("H", "uppercase", [line(0.22, 0.12, 0.22, 0.86), line(0.78, 0.12, 0.78, 0.86), line(0.22, 0.5, 0.78, 0.5, "crossbar")], { intersections: 2 }),
  letter("I", "uppercase", [line(0.25, 0.12, 0.75, 0.12, "crossbar"), line(0.5, 0.12, 0.5, 0.86), line(0.25, 0.86, 0.75, 0.86, "crossbar")], { intersections: 2 }),
  letter("J", "uppercase", [line(0.24, 0.12, 0.78, 0.12, "crossbar"), joined([line(0.64, 0.12, 0.64, 0.68), cubic(0.64, 0.68, 0.64, 0.93, 0.25, 0.93, 0.25, 0.7)])], { intersections: 1 }),
  letter("K", "uppercase", [line(0.24, 0.12, 0.24, 0.86), line(0.76, 0.12, 0.24, 0.52), line(0.24, 0.52, 0.8, 0.86)], { intersections: 2 }),
  letter("L", "uppercase", [line(0.25, 0.12, 0.25, 0.86), line(0.25, 0.86, 0.79, 0.86, "crossbar")], { intersections: 1 }),
  letter("M", "uppercase", [line(0.16, 0.12, 0.16, 0.86), line(0.16, 0.12, 0.5, 0.54), line(0.84, 0.12, 0.5, 0.54), line(0.84, 0.12, 0.84, 0.86)], { intersections: 3 }),
  letter("N", "uppercase", [line(0.2, 0.12, 0.2, 0.86), line(0.2, 0.12, 0.8, 0.86), line(0.8, 0.12, 0.8, 0.86)], { intersections: 2 }),
  letter("O", "uppercase", [ellipse(0.5, 0.49, 0.34, 0.38)], { enclosures: 1 }),
  letter("P", "uppercase", [line(0.25, 0.86, 0.25, 0.12), cubic(0.25, 0.12, 0.84, 0.08, 0.84, 0.51, 0.25, 0.49, "primary", true)], { enclosures: 1 }),
  letter("Q", "uppercase", [ellipse(0.5, 0.48, 0.33, 0.37), line(0.56, 0.66, 0.83, 0.91)], { enclosures: 1, intersections: 1 }),
  letter("R", "uppercase", [line(0.24, 0.86, 0.24, 0.12), cubic(0.24, 0.12, 0.83, 0.08, 0.83, 0.51, 0.24, 0.49, "primary", true), line(0.49, 0.49, 0.82, 0.86)], { enclosures: 1, intersections: 2 }),
  letter("S", "uppercase", [poly([[0.79, 0.2], [0.67, 0.11], [0.43, 0.1], [0.24, 0.22], [0.22, 0.39], [0.36, 0.48], [0.64, 0.53], [0.79, 0.64], [0.76, 0.8], [0.59, 0.89], [0.34, 0.88], [0.2, 0.78]])]),
  letter("T", "uppercase", [line(0.16, 0.12, 0.84, 0.12, "crossbar"), line(0.5, 0.12, 0.5, 0.86)], { intersections: 1 }),
  letter("U", "uppercase", [cubic(0.2, 0.12, 0.2, 0.91, 0.8, 0.91, 0.8, 0.12)]),
  letter("V", "uppercase", [line(0.16, 0.12, 0.5, 0.86), line(0.84, 0.12, 0.5, 0.86)], { intersections: 1 }),
  letter("W", "uppercase", [line(0.1, 0.12, 0.3, 0.86), line(0.5, 0.42, 0.3, 0.86), line(0.5, 0.42, 0.7, 0.86), line(0.9, 0.12, 0.7, 0.86)], { intersections: 3 }),
  letter("X", "uppercase", [line(0.19, 0.12, 0.81, 0.86), line(0.81, 0.12, 0.19, 0.86)], { intersections: 1 }),
  letter("Y", "uppercase", [line(0.17, 0.12, 0.5, 0.5), line(0.83, 0.12, 0.5, 0.5), line(0.5, 0.5, 0.5, 0.86)], { intersections: 2 }),
  letter("Z", "uppercase", [line(0.18, 0.12, 0.82, 0.12, "crossbar"), line(0.82, 0.12, 0.18, 0.86), line(0.18, 0.86, 0.82, 0.86, "crossbar")], { intersections: 2 }),
];

const LOWERCASE = [
  letterVariants("a", "lowercase", [
    variant("single-storey", [joined([
      cubic(0.67, 0.4, 0.55, 0.34, 0.33, 0.36, 0.24, 0.52),
      cubic(0.24, 0.52, 0.16, 0.69, 0.29, 0.82, 0.45, 0.82),
      cubic(0.45, 0.82, 0.6, 0.82, 0.67, 0.68, 0.67, 0.4),
      cubic(0.67, 0.4, 0.68, 0.55, 0.67, 0.7, 0.7, 0.82),
    ])], { enclosures: 1, intersections: 1 }),
    variant("double-storey", [
      ellipse(0.43, 0.67, 0.21, 0.15),
      joined([
        cubic(0.64, 0.78, 0.75, 0.66, 0.72, 0.45, 0.57, 0.41),
        cubic(0.57, 0.41, 0.41, 0.35, 0.26, 0.43, 0.3, 0.54),
        cubic(0.3, 0.54, 0.35, 0.63, 0.61, 0.59, 0.64, 0.48),
      ], "primary", true),
    ], { enclosures: 2, intersections: 1 }),
  ]),
  letter("b", "lowercase", [line(0.3, 0.12, 0.3, 0.82), ellipse(0.49, 0.61, 0.2, 0.21)], { enclosures: 1, intersections: 1 }),
  letter("c", "lowercase", [arc(0.5, 0.61, 0.24, 0.22, 315, 45)]),
  letter("d", "lowercase", [joined([
    cubic(0.69, 0.4, 0.56, 0.34, 0.34, 0.36, 0.25, 0.51),
    cubic(0.25, 0.51, 0.17, 0.68, 0.3, 0.82, 0.47, 0.82),
    cubic(0.47, 0.82, 0.62, 0.82, 0.69, 0.68, 0.69, 0.4),
    cubic(0.69, 0.4, 0.7, 0.3, 0.7, 0.2, 0.7, 0.12),
  ])], { enclosures: 1, intersections: 1 }),
  letter("e", "lowercase", [joined([
    cubic(0.27, 0.61, 0.39, 0.6, 0.58, 0.6, 0.7, 0.6),
    cubic(0.7, 0.6, 0.7, 0.46, 0.59, 0.37, 0.45, 0.38),
    cubic(0.45, 0.38, 0.29, 0.39, 0.22, 0.53, 0.25, 0.67),
    cubic(0.25, 0.67, 0.28, 0.82, 0.42, 0.87, 0.55, 0.84),
    cubic(0.55, 0.84, 0.66, 0.83, 0.72, 0.77, 0.75, 0.71),
  ])], { enclosures: 1, intersections: 1 }),
  letter("f", "lowercase", [
    joined([cubic(0.68, 0.18, 0.54, 0.08, 0.37, 0.17, 0.38, 0.34), line(0.38, 0.34, 0.38, 0.86)]),
    line(0.2, 0.43, 0.64, 0.43, "crossbar"),
  ], { intersections: 1 }),
  letterVariants("g", "lowercase", [
    variant("single-storey", [joined([
      cubic(0.67, 0.4, 0.55, 0.34, 0.34, 0.36, 0.25, 0.51),
      cubic(0.25, 0.51, 0.17, 0.68, 0.29, 0.81, 0.46, 0.81),
      cubic(0.46, 0.81, 0.6, 0.81, 0.67, 0.68, 0.67, 0.4),
      line(0.67, 0.4, 0.67, 0.83),
      cubic(0.67, 0.83, 0.65, 1, 0.36, 1, 0.3, 0.88),
    ])], { enclosures: 1, intersections: 1 }),
    variant("double-storey", [
      ellipse(0.47, 0.53, 0.21, 0.15),
      joined([
        cubic(0.65, 0.64, 0.72, 0.68, 0.7, 0.76, 0.62, 0.8),
        cubic(0.62, 0.8, 0.8, 0.84, 0.73, 0.99, 0.46, 0.98),
        cubic(0.46, 0.98, 0.19, 0.98, 0.2, 0.8, 0.38, 0.78),
      ], "primary", true),
    ], { enclosures: 2, intersections: 1 }),
  ]),
  letterVariants("h", "lowercase", [
    variant("school-print", [
      line(0.27, 0.12, 0.27, 0.82),
      joined([cubic(0.27, 0.55, 0.37, 0.35, 0.69, 0.35, 0.69, 0.58), line(0.69, 0.58, 0.69, 0.82)]),
    ], { intersections: 1 }),
    variant("continuous-hump", [joined([
      line(0.27, 0.12, 0.27, 0.82),
      line(0.27, 0.82, 0.27, 0.55),
      cubic(0.27, 0.55, 0.37, 0.35, 0.69, 0.35, 0.69, 0.58),
      line(0.69, 0.58, 0.69, 0.82),
    ])], { intersections: 1 }),
  ]),
  letter("i", "lowercase", [line(0.5, 0.39, 0.5, 0.82), dot(0.5, 0.22)], { detachedMarks: 1 }),
  letter("j", "lowercase", [joined([line(0.57, 0.39, 0.57, 0.84), cubic(0.57, 0.84, 0.56, 1, 0.29, 1, 0.25, 0.89)]), dot(0.57, 0.22)], { detachedMarks: 1 }),
  letter("k", "lowercase", [line(0.27, 0.12, 0.27, 0.82), line(0.69, 0.39, 0.27, 0.63), line(0.27, 0.63, 0.72, 0.82)], { intersections: 2 }),
  letter("l", "lowercase", [line(0.49, 0.12, 0.49, 0.82)]),
  letter("m", "lowercase", [
    line(0.2, 0.39, 0.2, 0.82),
    joined([cubic(0.2, 0.55, 0.28, 0.34, 0.49, 0.35, 0.49, 0.57), line(0.49, 0.57, 0.49, 0.82)]),
    joined([cubic(0.49, 0.55, 0.57, 0.34, 0.8, 0.35, 0.8, 0.57), line(0.8, 0.57, 0.8, 0.82)]),
  ], { intersections: 2 }),
  letter("n", "lowercase", [
    line(0.27, 0.39, 0.27, 0.82),
    joined([cubic(0.27, 0.55, 0.37, 0.34, 0.7, 0.35, 0.7, 0.58), line(0.7, 0.58, 0.7, 0.82)]),
  ], { intersections: 1 }),
  letter("o", "lowercase", [ellipse(0.5, 0.61, 0.24, 0.22)], { enclosures: 1 }),
  letter("p", "lowercase", [line(0.29, 0.39, 0.29, 0.97), ellipse(0.5, 0.59, 0.21, 0.2)], { enclosures: 1, intersections: 1 }),
  letter("q", "lowercase", [joined([
    cubic(0.69, 0.4, 0.56, 0.34, 0.34, 0.36, 0.25, 0.51),
    cubic(0.25, 0.51, 0.17, 0.68, 0.3, 0.82, 0.47, 0.82),
    cubic(0.47, 0.82, 0.62, 0.82, 0.69, 0.68, 0.69, 0.4),
    line(0.69, 0.4, 0.69, 0.97),
    line(0.69, 0.97, 0.82, 0.87),
  ])], { enclosures: 1, intersections: 1 }),
  letter("r", "lowercase", [line(0.32, 0.39, 0.32, 0.82), cubic(0.32, 0.55, 0.39, 0.36, 0.62, 0.35, 0.72, 0.46)], { intersections: 1 }),
  letter("s", "lowercase", [poly([[0.69, 0.45], [0.59, 0.38], [0.43, 0.39], [0.31, 0.47], [0.32, 0.57], [0.43, 0.62], [0.6, 0.65], [0.7, 0.72], [0.66, 0.8], [0.53, 0.84], [0.37, 0.82], [0.28, 0.76]])]),
  letter("t", "lowercase", [
    joined([line(0.48, 0.2, 0.48, 0.72), cubic(0.48, 0.72, 0.48, 0.83, 0.58, 0.87, 0.68, 0.79)]),
    line(0.27, 0.43, 0.69, 0.43, "crossbar"),
  ], { intersections: 1 }),
  letter("u", "lowercase", [cubic(0.27, 0.39, 0.27, 0.88, 0.69, 0.88, 0.69, 0.39), line(0.69, 0.39, 0.69, 0.82)], { intersections: 1 }),
  letter("v", "lowercase", [line(0.24, 0.39, 0.5, 0.82), line(0.5, 0.82, 0.76, 0.39)], { intersections: 1 }),
  letter("w", "lowercase", [line(0.13, 0.39, 0.31, 0.82), line(0.31, 0.82, 0.5, 0.5), line(0.5, 0.5, 0.69, 0.82), line(0.69, 0.82, 0.87, 0.39)], { intersections: 3 }),
  letter("x", "lowercase", [line(0.25, 0.39, 0.75, 0.82), line(0.75, 0.39, 0.25, 0.82)], { intersections: 1 }),
  letter("y", "lowercase", [line(0.23, 0.39, 0.49, 0.8), joined([line(0.76, 0.39, 0.49, 0.8), line(0.49, 0.8, 0.36, 0.97)])], { intersections: 1 }),
  letter("z", "lowercase", [line(0.25, 0.39, 0.75, 0.39, "crossbar"), line(0.75, 0.39, 0.25, 0.82), line(0.25, 0.82, 0.75, 0.82, "crossbar")], { intersections: 2 }),
];

const NUMBERS = [
  drawing("number-0", "Zero", "0", "number", 0.68, [ellipse(0.5, 0.49, 0.3, 0.38)], { enclosures: 1 }),
  drawing("number-1", "One", "1", "number", 0.68, [poly([[0.36, 0.27], [0.5, 0.12], [0.5, 0.86]])]),
  drawing("number-2", "Two", "2", "number", 0.68, [joined([
    cubic(0.22, 0.28, 0.28, 0.06, 0.75, 0.06, 0.78, 0.29),
    cubic(0.78, 0.29, 0.78, 0.43, 0.53, 0.57, 0.23, 0.84),
    line(0.23, 0.84, 0.8, 0.84, "crossbar"),
  ])]),
  drawing("number-3", "Three", "3", "number", 0.68, [joined([
    cubic(0.24, 0.2, 0.37, 0.05, 0.76, 0.08, 0.75, 0.31),
    cubic(0.75, 0.31, 0.74, 0.47, 0.57, 0.49, 0.43, 0.49),
    cubic(0.43, 0.49, 0.61, 0.49, 0.78, 0.54, 0.76, 0.7),
    cubic(0.76, 0.7, 0.73, 0.94, 0.34, 0.93, 0.22, 0.79),
  ])]),
  drawing("number-4", "Four", "4", "number", 0.68, [poly([[0.67, 0.86], [0.67, 0.12], [0.2, 0.63], [0.82, 0.63]])], { intersections: 1 }),
  drawing("number-5", "Five", "5", "number", 0.68, [joined([
    line(0.76, 0.12, 0.29, 0.12, "crossbar"),
    line(0.29, 0.12, 0.25, 0.48),
    cubic(0.25, 0.48, 0.71, 0.38, 0.82, 0.58, 0.76, 0.73),
    cubic(0.76, 0.73, 0.68, 0.94, 0.34, 0.93, 0.22, 0.79),
  ])]),
  drawing("number-6", "Six", "6", "number", 0.68, [joined([
    cubic(0.72, 0.16, 0.48, 0.04, 0.25, 0.25, 0.25, 0.56),
    cubic(0.25, 0.56, 0.24, 0.82, 0.39, 0.9, 0.52, 0.89),
    cubic(0.52, 0.89, 0.76, 0.89, 0.82, 0.72, 0.77, 0.58),
    cubic(0.77, 0.58, 0.71, 0.39, 0.4, 0.37, 0.25, 0.56),
  ])], { enclosures: 1 }),
  drawing("number-7", "Seven", "7", "number", 0.68, [joined([line(0.2, 0.12, 0.8, 0.12, "crossbar"), line(0.8, 0.12, 0.37, 0.86)])]),
  drawing("number-8", "Eight", "8", "number", 0.68, [joined([
    cubic(0.5, 0.12, 0.27, 0.12, 0.22, 0.36, 0.5, 0.49),
    cubic(0.5, 0.49, 0.78, 0.62, 0.77, 0.88, 0.5, 0.88),
    cubic(0.5, 0.88, 0.23, 0.88, 0.22, 0.63, 0.5, 0.49),
    cubic(0.5, 0.49, 0.78, 0.36, 0.75, 0.12, 0.5, 0.12),
  ])], { enclosures: 2, intersections: 1 }),
  drawing("number-9", "Nine", "9", "number", 0.68, [ellipse(0.49, 0.34, 0.27, 0.23), cubic(0.76, 0.34, 0.76, 0.64, 0.68, 0.8, 0.42, 0.88)], { enclosures: 1 }),
];

const LETTERS = [...UPPERCASE, ...LOWERCASE];
const LETTER_LOOKUP = Object.fromEntries(LETTERS.map((item) => [item.character, item]));
const NUMBER_LOOKUP = Object.fromEntries(NUMBERS.map((item) => [item.displayText, item]));
const DRAWING_LOOKUP = Object.fromEntries(NUMBERS.map((item) => [item.id, item]));

export function letterTemplate(character) {
  if (!character) return null;
  return LETTER_LOOKUP[character] || null;
}

export function drawingTemplateForCharacter(character) {
  const letterItem = letterTemplate(character);
  if (letterItem) {
    return {
      id: `character-${character}`,
      displayName: String(character),
      displayText: String(character),
      category: "letter",
      aspectRatio: 0.68,
      variants: letterItem.variants,
    };
  }
  return NUMBER_LOOKUP[character] || null;
}

export function drawingTemplateById(id) {
  if (DRAWING_LOOKUP[id]) return DRAWING_LOOKUP[id];
  if (typeof id === "string" && id.startsWith("character-")) {
    return drawingTemplateForCharacter(id.slice("character-".length)[0]);
  }
  return null;
}

const FORMATION_CACHE = new Map();

/**
 * Kindergarten formation strokes for the demo / guide start dots: ordered,
 * directional polylines in a 1×1 box. Prefers the hand-authored
 * GLYPH_STROKES (top-to-bottom, left-to-right, proper start points) and
 * falls back to the evaluator template when a glyph has no entry.
 * Each stroke: { points, start, prompt, kind }.
 */
export function formationStrokes(character) {
  const key = String(character || "")[0];
  if (!key) return [];
  if (FORMATION_CACHE.has(key)) return FORMATION_CACHE.get(key);
  const authored = GLYPH_STROKES[key];
  let strokes;
  if (authored?.strokes?.length) {
    strokes = authored.strokes
      .map((stroke) => {
        const points = svgPathToPolyline(stroke.d);
        if (points.length < 2) return null;
        return {
          points,
          start: stroke.start ? { x: stroke.start[0], y: stroke.start[1] } : points[0],
          prompt: stroke.prompt || "",
          kind: "primary",
          isClosed: false,
        };
      })
      .filter(Boolean);
  } else {
    const template = drawingTemplateForCharacter(key);
    strokes = (template?.variants[0]?.strokes || []).map((stroke) => ({
      points: stroke.points,
      start: stroke.points[0],
      prompt: "",
      kind: stroke.kind,
      isClosed: stroke.isClosed,
    }));
  }
  FORMATION_CACHE.set(key, strokes);
  return strokes;
}

export function formationHint(character) {
  return GLYPH_STROKES[String(character || "")[0]]?.hint || "";
}

export const REFERENCE_TEMPLATES = LETTER_LOOKUP;
export const NUMBER_TEMPLATES = NUMBERS;

export { LETTERS, NUMBERS };
